import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { formatCurrency, formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { calculateMonthlyEffectiveCompoundRate } from "./CommonChartUtils";

type GeneratePropertyPurchaseChartDataParams = {
  readonly loanAmount: number;
  readonly currentSavingsAmount: number;
  readonly accumulationPhaseDuration: number;
  readonly monthlyContributionAmount: number;
  readonly inflationRate: number;
  readonly interestRate: number;
  readonly loanDuration: number;
  readonly loanInterestRate: number;
  readonly investCurrentSavings: boolean;
};

type GenerateAccumulationPhaseDataParams = Pick<
  GeneratePropertyPurchaseChartDataParams,
  | "accumulationPhaseDuration"
  | "investCurrentSavings"
  | "currentSavingsAmount"
  | "inflationRate"
  | "interestRate"
  | "monthlyContributionAmount"
>;

type GenerateRepaymentPhaseDataParams = {
  readonly loanInterestRate: number;
  readonly loanAmount: number;
  readonly monthlyPaymentAmount: number;
  readonly repaymentPhaseDurationInYears: number;
};

type PropertyPurchaseChartData = {
  readonly accumulationPhaseData: number[];
  readonly repaymentPhaseData: number[];
};

type CalculateMonthlyContributionParams = {
  readonly targetAmount: number;
  readonly startAmount: number;
  readonly durationInMonths: number;
  readonly interestRate: number;
  readonly inflationRate: number;
  readonly investCurrentSavings: boolean;
};

type CalculateMonthlyPaymentParams = {
  readonly loanAmount: number;
  readonly durationInYears: number;
  readonly loanInterestRate: number;
};

type CalculateFinalOneTimeRepaymentAmountParams = {
  readonly loanAmount: number;
  readonly loanInterestRate: number;
  readonly durationInYears: number;
  readonly monthlyPaymentAmount: number;
};

type GetPropertyChartAnnotationsParams = {
  readonly loanAmount: number;
  readonly downpaymentAmount: number;
  readonly accumulationPhaseDuration: number;
  readonly totalDuration: number;
};

export const generatePropertyPurchaseChartData = ({
  currentSavingsAmount,
  monthlyContributionAmount,
  accumulationPhaseDuration,
  loanInterestRate,
  interestRate,
  inflationRate,
  loanAmount,
  loanDuration,
  investCurrentSavings,
}: GeneratePropertyPurchaseChartDataParams): PropertyPurchaseChartData => {
  const accumulationPhaseData = _generateAccumulationPhaseData({
    monthlyContributionAmount,
    inflationRate,
    interestRate,
    accumulationPhaseDuration,
    currentSavingsAmount,
    investCurrentSavings,
  });

  const monthlyPaymentAmount = calculateMonthlyPayment({
    loanInterestRate,
    loanAmount,
    durationInYears: loanDuration,
  });

  const repaymentPhaseData = new Array(accumulationPhaseData.length - 1).fill(Number.NaN).concat(
    _generateRepaymentPhaseData({
      loanAmount,
      monthlyPaymentAmount,
      loanInterestRate,
      repaymentPhaseDurationInYears: loanDuration,
    }),
  );

  return {
    accumulationPhaseData,
    repaymentPhaseData,
  };
};

export const calculateMonthlyContribution = ({
  targetAmount,
  startAmount,
  interestRate,
  inflationRate,
  durationInMonths,
  investCurrentSavings,
}: CalculateMonthlyContributionParams) => {
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(interestRate, inflationRate);

  const _targetAmount = investCurrentSavings ? targetAmount : targetAmount - startAmount;
  const _startAmount = investCurrentSavings ? startAmount : 0;

  // divide by zero prevention
  if (monthlyCompoundRate === 0) {
    return durationInMonths > 0 ? (_targetAmount - _startAmount) / durationInMonths : 0;
  }

  return (
    (_targetAmount - _startAmount * (1 + monthlyCompoundRate) ** durationInMonths) *
    (monthlyCompoundRate / (-1 + (1 + monthlyCompoundRate) ** durationInMonths))
  );
};

export const calculateMonthlyPayment = ({ loanAmount, loanInterestRate, durationInYears }: CalculateMonthlyPaymentParams) => {
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(loanInterestRate, 0);
  const durationInMonths = durationInYears * 12;

  // divide by zero prevention
  if (monthlyCompoundRate === 0) {
    return durationInMonths > 0 ? loanAmount / durationInMonths : 0;
  }

  return (
    loanAmount *
    ((monthlyCompoundRate * (1 + monthlyCompoundRate) ** durationInMonths) / ((1 + monthlyCompoundRate) ** durationInMonths - 1))
  );
};

export const calculateFinalOneTimeRepaymentAmount = ({
  loanAmount,
  loanInterestRate,
  durationInYears,
  monthlyPaymentAmount,
}: CalculateFinalOneTimeRepaymentAmountParams) => {
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(loanInterestRate, 0);
  const totalMonths = durationInYears * 12;

  // divide by zero prevention
  if (monthlyCompoundRate === 0) {
    return Number(loanAmount) - Number(monthlyPaymentAmount) * totalMonths;
  }

  const result =
    Number(loanAmount) * (1 + monthlyCompoundRate) ** totalMonths -
    Number(monthlyPaymentAmount) * (((1 + monthlyCompoundRate) ** totalMonths - 1) / monthlyCompoundRate);

  return result;
};

export const getPropertyChartAnnotations = ({ downpaymentAmount, loanAmount, totalDuration }: GetPropertyChartAnnotationsParams) => {
  const commonLineProps = {
    type: "line" as const,
    borderColor: "rgba(0, 0, 0, 0.05)",
    borderWidth: 0.8,
    borderDash: [6, 4],
  };

  const commonLabelProps = {
    display: true,
    backgroundColor: "transparent",
    color: colors.textSecondary,
    position: "start" as const,
    yAdjust: 10,
    font: {
      weight: 400,
      size: 10,
    },
  };

  return {
    startLine: {
      ...commonLineProps,
      borderColor: colors.borderLight,
      xMin: 0,
      xMax: 0,
      yMax: loanAmount,
      yMin: 0,
    },
    downpaymentCreatedLine: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(downpaymentAmount),
        color: colors.chartPositive,
      },
      ...commonLineProps,
      borderColor: colors.chartPositive,
      xMin: 0,
      xMax: totalDuration,
      yMax: downpaymentAmount,
      yMin: downpaymentAmount,
    },
    loanStartedLine: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(loanAmount),
        color: colors.chartNeutral,
      },
      ...commonLineProps,
      borderColor: colors.chartNeutral,
      xMin: 0,
      xMax: totalDuration,
      yMax: loanAmount,
      yMin: loanAmount,
    },
  };
};

const _generateAccumulationPhaseData = ({
  accumulationPhaseDuration,
  currentSavingsAmount,
  inflationRate,
  interestRate,
  monthlyContributionAmount,
  investCurrentSavings,
}: GenerateAccumulationPhaseDataParams): number[] => {
  const result: number[] = [];
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(interestRate, inflationRate);

  for (let i = 0; i < accumulationPhaseDuration + 1; i++) {
    let formula: number;

    // divide by zero prevention
    if (monthlyCompoundRate === 0) {
      formula = currentSavingsAmount + monthlyContributionAmount * i;
    } else {
      formula =
        (investCurrentSavings ? 0 : currentSavingsAmount) +
        ((investCurrentSavings ? currentSavingsAmount : 0) * (1 + monthlyCompoundRate) ** i +
          (monthlyContributionAmount * (-1 + (1 + monthlyCompoundRate) ** i)) / monthlyCompoundRate);
    }

    if (accumulationPhaseDuration > 30) {
      if (i % 6 === 0) {
        result.push(formula);
      }
    } else {
      result.push(formula);
    }
  }

  return result;
};

const _generateRepaymentPhaseData = ({
  repaymentPhaseDurationInYears,
  loanAmount,
  loanInterestRate,
  monthlyPaymentAmount,
}: GenerateRepaymentPhaseDataParams) => {
  const result: number[] = [];
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(loanInterestRate, 0);

  for (let i = 0; i < repaymentPhaseDurationInYears + 1; i++) {
    let formula: number;

    // divide by zero prevention
    if (monthlyCompoundRate === 0) {
      formula = loanAmount - monthlyPaymentAmount * (i * 12);
    } else {
      formula =
        loanAmount * (1 + monthlyCompoundRate) ** (i * 12) -
        monthlyPaymentAmount * (((1 + monthlyCompoundRate) ** (i * 12) - 1) / monthlyCompoundRate);
    }

    result.push(formula);
  }

  return result;
};
